'use client';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { alpha, Backdrop, Box, Fade, Modal, useTheme } from '@mui/material';

import { useAuth, useClobOrder, useMixpanel, useSignInHandlers } from '@/hooks';
import { useOrderBook, usePortfolioSummary } from '@/services/query';
import { useMarketPrices } from '@/services/query/markets.queries';
import { openModal, useAppDispatch } from '@/store';
import { setSetupRequested } from '@/store/slices/auth.slice';
import { EventMarket, EventOutcome, EventResponse, Market, Outcome } from '@/types';
import {
  calculateMidpointPrice,
  getNoSideFallbackPrice,
  isMarketResolved,
  normalizeMarketQuestion,
  resolveOutcomeData,
} from '@/utils';
import { MobileTradeContent } from './MobileTradeContent';
import { ResolvedTradeView } from './ResolvedTradeView';

/**
 * TradeModal - Main modal container for trading.
 * Handles display logic for markets and outcomes.
 */

export interface TradeModalProps {
  event?: EventResponse;
  market: Market | EventMarket;
  outcome: Outcome | EventOutcome;
  isBuyOrder: boolean;
  isOpen: boolean;
  onClose: () => void;
  teamColor?: string;
  forceDesktopVersion?: boolean;
}

export function TradeModal({
  event,
  market,
  outcome,
  isBuyOrder,
  isOpen,
  onClose,
}: TradeModalProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const dispatch = useAppDispatch();
  const { isAuthenticated, user, isReadyToTrade } = useAuth();
  const { data: portfolioSummary } = usePortfolioSummary();
  const { onLogin } = useSignInHandlers((error) => {
    if (error) console.error('Sign in error:', error);
  });

  const { trackTradeFlowStarted, trackTradeSubmitted, trackTradeConfirmed, trackTradeFailed } =
    useMixpanel();
  const { submitOrder, isSubmitting, error: orderError } = useClobOrder();

  const [activeTab, setActiveTab] = useState<'buy' | 'sell'>(isBuyOrder ? 'buy' : 'sell');
  const [amount, setAmount] = useState('10');
  const [sliderValue, setSliderValue] = useState(10);

  const isResolved = isMarketResolved(market);

  function handleSliderChange(newValue: number) {
    setSliderValue(newValue);
    setAmount(newValue.toString());
  }

  const amountNum = parseFloat(amount) || 0;

  function handleQuickAdd(v: number) {
    setAmount((prev) => ((parseFloat(prev) || 0) + v).toString());
  }

  const tokenIds = useMemo(() => {
    try {
      if (market && typeof market.clobTokenIds === 'string') {
        return JSON.parse(market.clobTokenIds);
      }
      return market?.clobTokenIds || [];
    } catch (e) {
      return [];
    }
  }, [market?.clobTokenIds]);

  const { data: realtimePrices } = useMarketPrices(tokenIds, isOpen);
  const { data: orderBook } = useOrderBook(outcome?.id || '', Boolean(outcome?.id && isOpen));

  const { price, priceNo } = useMemo(
    () => (market ? resolveOutcomeData(market as any) : { price: 0, priceNo: 0 }),
    [market]
  );

  const isNoOutcome = outcome?.name?.toLowerCase() === 'no';

  let currentPrice = 0;
  if (outcome) {
    currentPrice = calculateMidpointPrice(outcome);
  }
  if (realtimePrices?.[outcome?.id || '']?.midPrice) {
    currentPrice = realtimePrices[outcome?.id || ''].midPrice;
  } else if (isNoOutcome) {
    currentPrice = getNoSideFallbackPrice(priceNo, price) ?? 0;
  } else if (!isNoOutcome && price > 0) {
    currentPrice = price;
  }

  useEffect(() => {
    if (isOpen && market && outcome) {
      trackTradeFlowStarted({
        market_id: market.id || '',
        market_title: (market as any).question || (market as any).title || '',
        outcome_selected: outcome.name || outcome.id || '',
        current_price: currentPrice,
        trade_side: isBuyOrder ? 'buy' : 'sell',
        order_type: 'market',
        category: (market as any).category || (market as any).groupItemTitle || '',
      });
    }
  }, [isOpen]);

  if (!market || !outcome) return null;

  const balance = portfolioSummary?.cashBalance || 0;
  const isInsufficientBalance = activeTab === 'buy' && amountNum > balance && amountNum > 0;

  const isInputInvalid =
    isAuthenticated &&
    isReadyToTrade &&
    (amountNum <= 0 ||
      (activeTab === 'sell' && (!orderBook?.bids || orderBook.bids.length === 0)) ||
      (activeTab === 'buy' && (!orderBook?.asks || orderBook.asks.length === 0)));

  async function handlePrimaryAction() {
    if (!isAuthenticated) {
      onLogin();
      return;
    }
    if (!user?.isReferredUser) {
      dispatch(openModal('referral'));
      return;
    }
    if (!isReadyToTrade) {
      dispatch(setSetupRequested(true));
      return;
    }
    if (isInsufficientBalance) {
      dispatch(openModal('deposit'));
      return;
    }
    handleTrade();
  }

  async function handleTrade() {
    if (!isAuthenticated) {
      onLogin();
      return;
    }
    if (!user?.isReferredUser) {
      dispatch(openModal('referral'));
      return;
    }
    dispatch(setSetupRequested(true));

    const shares = currentPrice > 0 ? amountNum / currentPrice : 0;

    if (amountNum <= 0) {
      console.error('Invalid order amount');
      return;
    }

    try {
      const side = activeTab.toUpperCase() as 'BUY' | 'SELL';

      trackTradeSubmitted({
        market_id: market.id || '',
        outcome_selected: outcome.name || outcome.id || '',
        amount: amountNum,
        shares,
        price_per_share: currentPrice,
        trade_type: 'market',
        trade_side: activeTab,
        order_type: 'market',
        amount_preset_used: false,
        category: (market as any).category || (market as any).groupItemTitle || '',
      });

      const response = await submitOrder({
        tokenId: outcome.id,
        amount: side === 'BUY' ? amountNum : shares,
        side,
        userUSDCBalance: balance,
      });

      trackTradeConfirmed({
        market_id: market.id || '',
        trade_id: response?.orderId || 'unknown',
        outcome_selected: outcome.name || outcome.id || '',
        amount: amountNum,
        tx_hash: 'clob_no_tx',
        latency_ms: 0,
        trade_side: activeTab,
        order_type: 'market',
        category: (market as any).category || (market as any).groupItemTitle || '',
      });
      onClose();
    } catch (err) {
      console.error('Error placing order:', err);
      trackTradeFailed({
        market_id: market.id || '',
        outcome_selected: outcome.name || outcome.id || '',
        amount: amountNum,
        error_code: (err as any)?.message || 'unknown',
        trade_side: activeTab,
        order_type: 'market',
      });
    }
  }

  const modalContentStyle = {
    outline: 'none',
    width: { xs: 'calc(100% - 24px)', sm: 440, md: 480 },
    height: 'auto',
    maxHeight: { xs: '98vh', sm: '98vh', md: '96vh' },
    bgcolor: 'background.paper',
    borderRadius: { xs: '24px', sm: '32px' },
    overflow: 'hidden',
    boxShadow: isDark
      ? `0 32px 100px ${alpha(theme.palette.common.black, 0.85)}`
      : theme.shadows[10],
    border: '1px solid',
    borderColor: 'divider',
    display: 'flex',
    flexDirection: 'column',
  };

  return (
    <>
      <Modal
        open={isOpen}
        onClose={onClose}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
        }}
      >
        <Fade in={isOpen}>
          <Box sx={modalContentStyle}>
            {isResolved ? (
              <ResolvedTradeView event={event} market={market} />
            ) : (
              <MobileTradeContent
                event={event}
                market={market}
                outcome={outcome}
                onClose={onClose}
                amount={amount}
                setAmount={setAmount}
                sliderValue={sliderValue}
                onSliderChange={handleSliderChange}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                price={currentPrice}
                onTrade={handlePrimaryAction}
                onQuickAdd={handleQuickAdd}
                isSubmitting={isSubmitting}
                orderError={orderError}
                isAuthenticated={isAuthenticated}
                isReadyToTrade={isReadyToTrade}
                isInsufficientBalance={isInsufficientBalance}
                isInputInvalid={isInputInvalid}
                balance={balance}
                orderBook={orderBook}
              />
            )}
          </Box>
        </Fade>
      </Modal>
    </>
  );
}

export default TradeModal;
